<template>
  <div>
    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Create Order
            </h3>

            <p>
              Enter the details of the order.
            </p>
          </help-text>
        </div>

        <div class="col-xs-18">
          <form @submit.prevent="submit" class="form-horizontal">
            <div class="panel panel-default">
              <div class="panel-body">
                <text-form-group
                    v-model="order.title"
                    name="title"
                    label="Title"
                    help-text="The title of the order."
                    :error="getError('title')"
                ></text-form-group>

                <text-form-group
                    v-model="order.purchase_order"
                    name="purchase_order"
                    label="Purchase Order (PO number)"
                    help-text="The company's purchase order (not required)."
                    :error="getError('purchase_order')"
                ></text-form-group>

                <select-form-group
                    v-model="order.user_id"
                    name="user_id"
                    label="User"
                    help-text="The user that is responsible for the order."
                    :error="getError('user_id')"
                    :options="userOptions"
                ></select-form-group>

                <select-form-group
                    v-model="order.opportunity_id"
                    name="opportunity_id"
                    label="Opportunity"
                    help-text="The opportunity that is converted into this order."
                    :error="getError('opportunity_id')"
                    :options="opportunityOptions"
                    :nullable="true"
                ></select-form-group>

                <select-with-button-form-group
                    v-model="order.contact_person_id"
                    name="contact_person_id"
                    label="Contact Person"
                    help-text="The contact person in the company."
                    :error="getError('contact_person_id')"
                    :options="personOptions"
                    @click="openPersonModal"
                ></select-with-button-form-group>

                <typeahead-form-group
                    v-model="order.content_responsible_id"
                    name="content_responsible_id"
                    label="Content Responsible"
                    help-text="The person that is responsible for the content of the ads."
                    :error="getError('content_responsible_id')"
                    endpoint="/api/v2/people"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :result-label-property="person => person.attributes.name + (person.attributes.company_id ? ' (' + person.attributes.company_name + ')' : '')"
                    :initial-label="contentResponsibleName"
                ></typeahead-form-group>

                <typeahead-form-group
                    v-model="order.other_payer_id"
                    name="other_payer_id"
                    label="Other Payer"
                    help-text="The company that pays for the ad (if not the company itself)."
                    :error="getError('other_payer_id')"
                    endpoint="/api/v2/companies"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :result-label-property="row => row.attributes.name"
                ></typeahead-form-group>

                <typeahead-form-group
                    v-model="order.other_payer_contact_person_id"
                    name="other_payer_contact_person_id"
                    label="Other Payer Contact Person"
                    help-text="The contact person in the company that pays for the ad (if not the company itself)."
                    :error="getError('other_payer_contact_person_id')"
                    endpoint="/api/v2/people"
                    :params="{'filter[company_id]': order.other_payer_id}"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :result-label-property="row => row.attributes.name"
                ></typeahead-form-group>

                <hr>

                <submit-form-group button-class="btn-black"></submit-form-group>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <create-person-modal :company-id="company.data.id" @created="personCreated"></create-person-modal>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import store from '@/store'
import CreatePersonModal from '@/pages/Companies/Companies/Edit/Modals/CreatePerson'
import Errors from '@/mixins/Errors'
import HelpText from '@/components/HelpText'
import Load from '@/mixins/Load'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import SelectWithButtonFormGroup from '@/components/FormGroups/Horizontal/SelectWithButton'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import TypeaheadFormGroup from '@/vendor/forms/FormGroups/Horizontal/Typeahead'

export default {
  components: {
    CreatePersonModal,
    HelpText,
    LoadingContainer,
    SelectFormGroup,
    SelectWithButtonFormGroup,
    SubmitFormGroup,
    TextFormGroup,
    TypeaheadFormGroup
  },

  mixins: [Errors, Load],

  model: {
    prop: 'company',
    event: 'company-updated'
  },

  props: ['company'],

  data() {
    return {
      shared: store,
      order: {
        contact_person_id: this.company.data.advertising_contact_id,
        company_id: this.company.data.id,
        opportunity_id: this.getQueryParam('opportunity'),
        user_id: store.user.id
      },
      users: [],
      opportunities: [],
      people: [],
      contentResponsibleName: null
    }
  },

  mounted() {
    this.load({
      users: '/api/v2/users',
      opportunities: '/api/v2/opportunities?filter[status]=open&filter[company_id]=' + this.company.data.id,
      people: '/api/v2/people?page_size=100&filter[company_id]=' + this.company.data.id,
    })
  },

  computed: {
    userOptions() {
      return this.users.data.map(user => {
        return {
          value: user.id,
          label: user.attributes.full_name
        }
      })
    },

    personOptions() {
      return this.people.data
          .filter(person => person.attributes.is_active)
          .map(person => {
            return {
              value: person.id,
              label: person.attributes.name
            }
          })
    },

    opportunityOptions() {
      return this.opportunities.data.map(opportunity => {
        return {
          value: opportunity.id,
          label: opportunity.attributes.user_full_name + ' - ' + this.$options.filters.date(opportunity.attributes.conversion_expected_at) + ' - ' + this.$options.filters.price(opportunity.attributes.value)
        }
      })
    },
  },

  methods: {
    submit() {
      http.post('/api/v1/orders', this.order)
          .then(response => {
            notify.success('The order was saved')

            this.$router.push('./' + response.data.data.id)
          })
          .catch(error => {
            if (error.response.status === 400) {
              return notify.error(error.response.data.message)
            }

            throw error
          })
          .catch(this.catchValidationErrors)
    },

    openPersonModal() {
      $('#create-person-modal').modal('show')
    },

    personCreated(person) {
      this.people.data.push(person)

      this.people.data.sort((a, b) => a.attributes.name.toLowerCase() >= b.attributes.name.toLowerCase() ? 1 : -1)

      this.$nextTick(() => {
        this.order.contact_person_id = person.id
      })
    },

    getQueryParam(key, defaultValue = null) {
      return typeof this.$route.query[key] !== 'undefined' ? this.$route.query[key] : defaultValue
    }
  }
}
</script>
